// ═══════════════════════════════════════════════════════════
// UniFi Switch SVG Factory Module
// Photo-realistic SVG front panels for UniFi switch models.
// Ported from approved HTML preview: unifi_switch_factory_preview.html
// ═══════════════════════════════════════════════════════════

export interface SwitchConfig {
  ports: number
  cols: number
  rows: number
  sfpCages: number
  hasPoe: boolean
  hasLcd: boolean
  family: string
  mm: { width: number; height: number; depth: number }
}

export const PORT_CONFIGS: Record<string, SwitchConfig> = {
  "USW-Pro-48-PoE": {
    ports: 48, cols: 24, rows: 2, sfpCages: 4, hasPoe: true, hasLcd: true,
    family: 'pro-rack', mm: { width: 442, height: 44, depth: 400 }
  },
  "USW-Pro-24-PoE": {
    ports: 24, cols: 12, rows: 2, sfpCages: 2, hasPoe: true, hasLcd: true,
    family: 'pro-rack', mm: { width: 442, height: 44, depth: 285 }
  },
  "USW-Lite-16-PoE": {
    ports: 16, cols: 8, rows: 2, sfpCages: 0, hasPoe: true, hasLcd: false,
    family: 'lite-desktop', mm: { width: 192, height: 44, depth: 185 }
  },
  "USW-Flex-Mini": {
    ports: 5, cols: 5, rows: 1, sfpCages: 0, hasPoe: false, hasLcd: false,
    family: 'flex-mini', mm: { width: 107, height: 21, depth: 70 }
  },
}

export interface PortPosition {
  index: number
  x: number
  y: number
  width: number
  height: number
  type: 'rj45' | 'sfp'
}

export interface UniFiSwitchSVG {
  model: string
  renderFront(): string
  renderBack(): string
  getPortPositions(): PortPosition[]
  viewBox: { width: number; height: number }
}

// ── Constants ────────────────────────────────────────────

const MAX_PHYSICAL_WIDTH_MM = 442
const MAX_RENDER_WIDTH = 760
const PX_PER_MM = MAX_RENDER_WIDTH / MAX_PHYSICAL_WIDTH_MM
const RJ45_FACE_W_MM = 11.7
const RJ45_GAP_MM = 1.8

function mm(v: number): number { return v * PX_PER_MM }

// ── Device model mapping ─────────────────────────────────

export function getModelForDevice(device: { model?: string; mpn?: string | null }): string | null {
  const raw = device.mpn || device.model
  if (!raw) return null
  const normalized = raw.toUpperCase().replace(/[\s_]+/g, '-')
  for (const key of Object.keys(PORT_CONFIGS)) {
    // Exact match
    if (key === raw) return key
    // Case-insensitive match
    if (key.toUpperCase() === normalized) return key
  }
  // Partial match only if the input is long enough to avoid false positives
  if (normalized.length >= 8) {
    for (const key of Object.keys(PORT_CONFIGS)) {
      if (normalized.includes(key.toUpperCase()) || key.toUpperCase().includes(normalized)) return key
    }
  }
  return null
}

// ── SVG Factory ──────────────────────────────────────────

export function makeUniFiSwitchModule(model: string): UniFiSwitchSVG {
  const cfg = PORT_CONFIGS[model]
  if (!cfg) throw new Error(`Unknown UniFi model: ${model}`)

  const id = model.replace(/[^a-z0-9]/gi, '_')
  const WIDTH = Math.round(cfg.mm.width * PX_PER_MM)
  const HEIGHT = Math.max(36, Math.round(cfg.mm.height * PX_PER_MM))

  const isRack = cfg.family === 'pro-rack'
  const hasEars = isRack
  const earW = hasEars ? 18 : 0
  const bodyX = earW
  const bodyY = 2
  const bodyW = WIDTH - earW * 2
  const bodyH = HEIGHT - 4

  // Collect port positions during render
  const portPositions: PortPosition[] = []

  function phillipsScrew(cx: number, cy: number, r = 2.3): string {
    return `
      <g>
        <circle cx="${cx}" cy="${cy}" r="${r}" fill="#9ca3af" stroke="#4b5563" stroke-width="0.6"/>
        <line x1="${cx - 1.2}" y1="${cy}" x2="${cx + 1.2}" y2="${cy}" stroke="#374151" stroke-width="0.7" stroke-linecap="round"/>
        <line x1="${cx}" y1="${cy - 1.2}" x2="${cx}" y2="${cy + 1.2}" stroke="#374151" stroke-width="0.7" stroke-linecap="round"/>
      </g>
    `
  }

  function _rackEar(x: number, side: 'left' | 'right' = 'left'): string {
    return `
      <g>
        <rect x="${x}" y="4" width="${earW}" height="${HEIGHT - 8}" rx="2" fill="#d9dce2" stroke="#bfc6cf" stroke-width="0.6"/>
        <circle cx="${x + (side === 'left' ? 4.6 : earW - 4.6)}" cy="${HEIGHT / 2}" r="2.3" fill="#eef1f5" stroke="#b4bbc6" stroke-width="0.5"/>
        ${phillipsScrew(x + earW / 2, 12)}
        ${phillipsScrew(x + earW / 2, HEIGHT - 12)}
      </g>
    `
  }

  function bodyShell(): string {
    if (cfg.family === 'pro-rack') {
      return `
        <g>
          <rect x="${bodyX}" y="${bodyY}" width="${bodyW}" height="${bodyH}" rx="2.8" fill="url(#bodyGradient_${id})" stroke="#bcc3cd" stroke-width="0.8"/>
          <rect x="${bodyX}" y="${bodyY}" width="${bodyW}" height="1" fill="#3b82f6"/>
          <rect x="${bodyX + 1}" y="${bodyY + 1}" width="${bodyW - 2}" height="7" fill="url(#topSheen_${id})" opacity="0.5"/>
          <rect x="${bodyX + 1}" y="${bodyY + bodyH - 3}" width="${bodyW - 2}" height="1.6" fill="#c5ccd5" opacity="0.45"/>
        </g>
      `
    }
    if (cfg.family === 'lite-desktop') {
      return `
        <g>
          <rect x="${bodyX}" y="${bodyY}" width="${bodyW}" height="${bodyH}" rx="18" fill="url(#whiteBody_${id})" stroke="#d2d7df" stroke-width="0.8"/>
          <rect x="${bodyX + 1}" y="${bodyY + 1}" width="${bodyW - 2}" height="4" rx="16" fill="rgba(255,255,255,0.7)"/>
          <rect x="${bodyX + 10}" y="${bodyY + 5}" width="${bodyW - 20}" height="1" fill="#eef2f7" opacity="0.7"/>
        </g>
      `
    }
    return `
      <g>
        <rect x="${bodyX}" y="${bodyY}" width="${bodyW}" height="${bodyH}" rx="14" fill="url(#whiteBody_${id})" stroke="#d8dde5" stroke-width="0.8"/>
        <rect x="${bodyX + 1}" y="${bodyY + 1}" width="${bodyW - 2}" height="3" rx="12" fill="rgba(255,255,255,0.72)"/>
      </g>
    `
  }

  function unifiLogo(): string {
    if (cfg.family === 'pro-rack') {
      return `
        <g opacity="0.22" transform="translate(${bodyX + bodyW / 2 - 16}, ${bodyY + 6})">
          <text x="0" y="0" font-size="7" font-family="Inter, Arial, sans-serif" fill="#8f98a6" letter-spacing="1.2">U</text>
          <text x="8" y="0" font-size="7" font-family="Inter, Arial, sans-serif" fill="#8f98a6" letter-spacing="0.8">niFi</text>
        </g>
      `
    }
    return `
      <g opacity="0.45">
        <text x="${bodyX + bodyW / 2}" y="${bodyY + (cfg.family === 'flex-mini' ? 12 : 13)}" text-anchor="middle" font-size="${cfg.family === 'flex-mini' ? 10 : 11}" font-family="Inter, Arial, sans-serif" fill="#b7bec9">U</text>
      </g>
    `
  }

  function drawRJ45Port(p: { x: number; y: number; width: number; height: number }, index: number, numberSize: number, dense = false): string {
    const ledR = dense ? 0.8 : 1.0
    const contact = dense
      ? `<rect x="${p.x + 3}" y="${p.y + 1}" width="${Math.max(3, p.width - 6)}" height="1.2" fill="#d4a72c"/>`
      : Array.from({ length: 8 }, (_, n) => {
          const pinRegionW = Math.max(10, p.width - 10)
          const pinW = Math.max(0.6, Math.min(1.2, pinRegionW / 15))
          const pinGap = (pinRegionW - pinW * 8) / 7
          const pinStartX = p.x + (p.width - pinRegionW) / 2
          const px = pinStartX + n * (pinW + pinGap)
          return `<rect x="${px}" y="${p.y + 1.15}" width="${pinW}" height="1.35" rx="0.2" fill="#d4a72c"/>`
        }).join('')

    const ledInset = dense ? 2.0 : 2.3
    const labelY = p.y + p.height + (dense ? 3.1 : 4.0)

    return `
      <g data-port="${index}">
        <circle cx="${p.x + ledInset}" cy="${p.y + 1.6}" r="${ledR}" fill="#22c55e" opacity="0.92"/>
        <circle cx="${p.x + p.width - ledInset}" cy="${p.y + 1.6}" r="${ledR}" fill="#f59e0b" opacity="${index % 3 === 0 ? 0.9 : 0.35}"/>
        <rect x="${p.x}" y="${p.y}" width="${p.width}" height="${p.height}" rx="0.9" fill="#0c1117" stroke="#414857" stroke-width="0.45"/>
        <rect x="${p.x + 1.8}" y="${p.y + 1.5}" width="${Math.max(4, p.width - 3.6)}" height="${Math.max(4, p.height - 3)}" rx="0.5" fill="#05080c"/>
        <rect x="${p.x + 2}" y="${p.y + 1.5}" width="${Math.max(3.6, p.width - 4)}" height="1.8" rx="0.3" fill="#1a212b" opacity="0.92"/>
        ${contact}
        <rect x="${p.x + Math.max(2.2, p.width * 0.14)}" y="${p.y + p.height - 2.3}" width="${Math.max(4, p.width - Math.max(4.5, p.width * 0.28))}" height="0.9" rx="0.3" fill="#131821"/>
        <text x="${p.x + p.width / 2}" y="${labelY}" text-anchor="middle" font-size="${numberSize}" font-family="Inter, Arial, sans-serif" fill="#9aa3b0">${index}</text>
      </g>
    `
  }

  // ── Pro-Rack Front ─────────────────────────────────────

  function renderProRackFront(): string {
    portPositions.length = 0

    const lcd = {
      x: bodyX + 14,
      y: bodyY + 10,
      w: cfg.ports === 48 ? 38 : 38,
      h: cfg.ports === 48 ? 38 : 38
    }

    const portW = cfg.ports === 48 ? 18.25 : 20.4
    const portH = cfg.ports === 48 ? 13.4 : 14.2
    const gapX = cfg.ports === 48 ? 2.55 : 3.1
    const gapY = 3.75
    const cols = cfg.cols
    const rows = cfg.rows
    const clusterW = cols * portW + (cols - 1) * gapX
    const clusterH = rows * portH + (rows - 1) * gapY

    const clusterLeft = cfg.ports === 48 ? bodyX + 82 : bodyX + 318
    const clusterTop = cfg.ports === 48 ? bodyY + 15.2 : bodyY + 16.0
    const bezelPadX = cfg.ports === 48 ? 4.2 : 5.2
    const bezelPadY = 4.2

    const portBlock = {
      x: clusterLeft - bezelPadX,
      y: clusterTop - bezelPadY,
      w: clusterW + bezelPadX * 2,
      h: clusterH + bezelPadY * 2,
      clusterLeft,
      clusterTop
    }

    const sfpW = cfg.ports === 48 ? 24 : 22
    const sfpH = cfg.ports === 48 ? 18 : 18
    const sfpGapX = cfg.ports === 48 ? 3.2 : 4
    const sfpGapY = 4
    const sfpLeft = cfg.ports === 48 ? bodyX + bodyW - 78 : bodyX + bodyW - 46
    const sfpTop = cfg.ports === 48 ? bodyY + 11.8 : bodyY + 16.8
    const resetX = bodyX + bodyW - 11.5
    const resetY = bodyY + bodyH - 7

    const slitY = bodyY + 6.2
    const slitH = 2.2
    const slit1 = { x: lcd.x + lcd.w + 11, w: cfg.ports === 48 ? 158 : 106 }
    const slit2 = { x: slit1.x + slit1.w + 10, w: cfg.ports === 48 ? 154 : 102 }
    const slit3 = cfg.ports === 48
      ? { x: slit2.x + slit2.w + 10, w: 148 }
      : { x: slit2.x + slit2.w + 10, w: 150 }

    // Pro-rack has a translate(0,2) offset on the content group
    const translateY = 2

    function faceNumber(n: number): string {
      if (cfg.ports === 48) return n === 1 ? '1*' : n === 2 ? '2*' : `${n}*`
      return n === 1 ? '1*' : `${n}*`
    }

    function portPos(index: number) {
      const i = index - 1
      const row = Math.floor(i / cols)
      const col = i % cols
      return {
        x: clusterLeft + col * (portW + gapX),
        y: clusterTop + row * (portH + gapY),
        width: portW,
        height: portH,
        row,
        col
      }
    }

    function drawProPort(index: number): string {
      const p = portPos(index)
      const dense = cfg.ports === 48
      const ledR = dense ? 0.8 : 0.9
      const upperY = p.y - 2.4
      const lowerY = p.y + p.height + 4.3
      const label = faceNumber(index)
      const contact = dense
        ? `<rect x="${p.x + 2.5}" y="${p.y + 1.2}" width="${Math.max(5, p.width - 5)}" height="1.2" fill="#cfa83a"/>`
        : Array.from({ length: 8 }, (_, n) => {
            const pinRegionW = Math.max(10, p.width - 8)
            const pinW = Math.max(0.6, Math.min(1.15, pinRegionW / 15))
            const pinGap = (pinRegionW - pinW * 8) / 7
            const pinStartX = p.x + (p.width - pinRegionW) / 2
            const px = pinStartX + n * (pinW + pinGap)
            return `<rect x="${px}" y="${p.y + 1.25}" width="${pinW}" height="1.35" rx="0.15" fill="#cfa83a"/>`
          }).join('')

      // Track port position (account for the translate(0,2) offset)
      portPositions.push({
        index,
        x: p.x,
        y: p.y + translateY,
        width: p.width,
        height: p.height,
        type: 'rj45'
      })

      return `
        <g data-port="${index}">
          <text x="${p.x + p.width / 2}" y="${p.row === 0 ? upperY : lowerY}" text-anchor="middle" font-size="2.15" font-family="Inter, Arial, sans-serif" fill="#666d79">${label}</text>
          <rect x="${p.x}" y="${p.y}" width="${p.width}" height="${p.height}" rx="0.7" fill="#111317" stroke="#575e68" stroke-width="0.42"/>
          <rect x="${p.x + 1.2}" y="${p.y + 1.2}" width="${p.width - 2.4}" height="${p.height - 2.3}" rx="0.45" fill="#080a0d"/>
          <rect x="${p.x + 1.3}" y="${p.y + 1.2}" width="${p.width - 2.6}" height="1.8" rx="0.25" fill="#2a3037" opacity="0.95"/>
          ${contact}
          <rect x="${p.x + Math.max(1.8, p.width * 0.14)}" y="${p.y + p.height - 2.4}" width="${Math.max(4, p.width - Math.max(3.8, p.width * 0.28))}" height="0.85" rx="0.25" fill="#141920"/>
          <circle cx="${p.x + 1.9}" cy="${p.y + 1.8}" r="${ledR}" fill="#93d53d" opacity="0.96"/>
          <circle cx="${p.x + p.width - 1.9}" cy="${p.y + 1.8}" r="${ledR}" fill="#9adf4d" opacity="0.82"/>
        </g>
      `
    }

    let ports = ''
    for (let i = 1; i <= cfg.ports; i++) ports += drawProPort(i)

    let sfp = ''
    if (cfg.sfpCages === 4) {
      for (let i = 0; i < 4; i++) {
        const col = i % 2
        const row = Math.floor(i / 2)
        const x = sfpLeft + col * (sfpW + sfpGapX)
        const y = sfpTop + row * (sfpH + sfpGapY)
        const num = 49 + i
        portPositions.push({ index: num, x, y: y + translateY, width: sfpW, height: sfpH, type: 'sfp' })
        sfp += `
          <g data-port="${num}">
            <text x="${x + sfpW / 2}" y="${row === 0 ? y - 2.2 : y + sfpH + 4.2}" text-anchor="middle" font-size="2.15" font-family="Inter, Arial, sans-serif" fill="#666d79">${num}</text>
            <rect x="${x}" y="${y}" width="${sfpW}" height="${sfpH}" rx="1.1" fill="#4d5159" stroke="#6d737d" stroke-width="0.45"/>
            <rect x="${x + 1.2}" y="${y + 1.2}" width="${sfpW - 2.4}" height="${sfpH - 2.4}" rx="0.8" fill="#272c33"/>
            <rect x="${x + 3.6}" y="${y + 4.5}" width="${sfpW - 7.2}" height="1.1" fill="#9ea4ad" opacity="0.72"/>
            <path d="M ${x + sfpW / 2 - 2.4} ${y + sfpH / 2 - 0.5} L ${x + sfpW / 2 + 2.4} ${y + sfpH / 2 - 0.5} L ${x + sfpW / 2} ${y + sfpH / 2 + 2.1} Z" fill="#dfe3e8" opacity="0.9"/>
          </g>`
      }
    } else {
      for (let i = 0; i < 2; i++) {
        const x = sfpLeft
        const y = sfpTop + i * (sfpH + sfpGapY)
        const num = 25 + i
        portPositions.push({ index: num, x, y: y + translateY, width: sfpW, height: sfpH, type: 'sfp' })
        sfp += `
          <g data-port="${num}">
            <text x="${x + sfpW / 2}" y="${i === 0 ? y - 2.2 : y + sfpH + 4.2}" text-anchor="middle" font-size="2.15" font-family="Inter, Arial, sans-serif" fill="#666d79">${num}</text>
            <rect x="${x}" y="${y}" width="${sfpW}" height="${sfpH}" rx="1.1" fill="#4d5159" stroke="#6d737d" stroke-width="0.45"/>
            <rect x="${x + 1.2}" y="${y + 1.2}" width="${sfpW - 2.4}" height="${sfpH - 2.4}" rx="0.8" fill="#272c33"/>
            <rect x="${x + 3.6}" y="${y + 4.5}" width="${sfpW - 7.2}" height="1.1" fill="#9ea4ad" opacity="0.72"/>
            <path d="M ${x + sfpW / 2 - 2.4} ${y + sfpH / 2 - 0.5} L ${x + sfpW / 2 + 2.4} ${y + sfpH / 2 - 0.5} L ${x + sfpW / 2} ${y + sfpH / 2 + 2.1} Z" fill="#dfe3e8" opacity="0.9"/>
          </g>`
      }
    }

    const reset = `
      <g>
        <circle cx="${resetX}" cy="${resetY}" r="1.35" fill="#666d79"/>
        <text x="${resetX + 4.1}" y="${resetY + 1.1}" font-size="2.35" font-family="Inter, Arial, sans-serif" fill="#666d79">RESET</text>
      </g>`

    const reflection = `
      <linearGradient id="reflection_${id}" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="rgba(255,255,255,0.25)"/>
        <stop offset="100%" stop-color="rgba(255,255,255,0)"/>
      </linearGradient>`

    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${WIDTH} ${HEIGHT + 34}" width="${WIDTH}" height="${HEIGHT + 34}" shape-rendering="geometricPrecision">
        <defs>
          <linearGradient id="bodyGradient_${id}" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#d8dadd"/>
            <stop offset="55%" stop-color="#cfd2d6"/>
            <stop offset="100%" stop-color="#c4c8cd"/>
          </linearGradient>
          <linearGradient id="lcdGradient_${id}" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#040b2c"/>
            <stop offset="100%" stop-color="#030616"/>
          </linearGradient>
          <linearGradient id="topSheen_${id}" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="rgba(255,255,255,0.35)"/>
            <stop offset="100%" stop-color="rgba(255,255,255,0)"/>
          </linearGradient>
          ${reflection}
        </defs>
        <g transform="translate(0,2)">
          <rect x="${bodyX}" y="${bodyY}" width="${bodyW}" height="${bodyH}" rx="1.6" fill="url(#bodyGradient_${id})" stroke="#989ea6" stroke-width="0.7"/>
          <rect x="${bodyX + 1}" y="${bodyY + 1}" width="${bodyW - 2}" height="5" fill="rgba(255,255,255,0.18)" opacity="0.5"/>
          <rect x="${slit1.x}" y="${slitY}" width="${slit1.w}" height="${slitH}" rx="1.2" fill="#20242a"/>
          <rect x="${slit2.x}" y="${slitY}" width="${slit2.w}" height="${slitH}" rx="1.2" fill="#20242a"/>
          <rect x="${slit3.x}" y="${slitY}" width="${slit3.w}" height="${slitH}" rx="1.2" fill="#20242a"/>

          <g>
            <rect x="${lcd.x}" y="${lcd.y}" width="${lcd.w}" height="${lcd.h}" rx="4.2" fill="url(#lcdGradient_${id})" stroke="#2e3962" stroke-width="0.55"/>
            <circle cx="${lcd.x + lcd.w / 2}" cy="${lcd.y + lcd.h / 2}" r="7.8" fill="#102367" stroke="#8ea6ff" stroke-width="0.6"/>
            <circle cx="${lcd.x + lcd.w / 2}" cy="${lcd.y + lcd.h / 2}" r="2.6" fill="#c6d8ff"/>
            <circle cx="${lcd.x + 7.2}" cy="${lcd.y + 8.2}" r="2.2" fill="none" stroke="#d9e4ff" stroke-width="0.55"/>
            <circle cx="${lcd.x + lcd.w - 7.2}" cy="${lcd.y + 8.2}" r="2.2" fill="none" stroke="#d9e4ff" stroke-width="0.55"/>
            <circle cx="${lcd.x + 7.2}" cy="${lcd.y + lcd.h - 8.2}" r="2.2" fill="none" stroke="#d9e4ff" stroke-width="0.55"/>
            <circle cx="${lcd.x + lcd.w - 7.2}" cy="${lcd.y + lcd.h - 8.2}" r="2.2" fill="none" stroke="#d9e4ff" stroke-width="0.55"/>
            <rect x="${lcd.x + lcd.w - 10}" y="${lcd.y + 5.8}" width="3.3" height="5.6" fill="#d9e4ff" opacity="0.95"/>
            <rect x="${lcd.x + 5.8}" y="${lcd.y + 5.8}" width="5.2" height="3.1" fill="none" stroke="#d9e4ff" stroke-width="0.5"/>
            <text x="${lcd.x + 7}" y="${lcd.y + lcd.h + 11}" font-size="4.3" font-family="Inter, Arial, sans-serif" fill="#4d5460">USW Pro</text>
          </g>

          <g>
            <rect x="${portBlock.x}" y="${portBlock.y}" width="${portBlock.w}" height="${portBlock.h}" rx="1.0" fill="#cfd2d5" stroke="#b1b6be" stroke-width="0.45"/>
            <rect x="${portBlock.x + 1}" y="${portBlock.y + 1}" width="${portBlock.w - 2}" height="${portBlock.h - 2}" rx="0.7" fill="#bcc0c6" opacity="0.28"/>
            ${ports}
          </g>

          ${sfp}
          ${reset}
        </g>
        <g transform="translate(0,${HEIGHT + 3})" opacity="0.24">
          <rect x="${bodyX + 8}" y="0" width="${bodyW - 16}" height="1" fill="url(#reflection_${id})"/>
          <rect x="${bodyX + 24}" y="1" width="${bodyW - 48}" height="14" fill="url(#reflection_${id})" opacity="0.45"/>
        </g>
      </svg>
    `
  }

  // ── Lite-16 Front ──────────────────────────────────────

  function renderLite16Front(): string {
    portPositions.length = 0

    const blueDot = { x: bodyX + 13, y: bodyY + 16 }
    const portW = Math.round(mm(RJ45_FACE_W_MM))
    const portH = 13
    const gapX = Math.round(mm(RJ45_GAP_MM) * 10) / 10
    const gapY = 4
    const blockX = bodyX + 18
    const topY = bodyY + 30
    const rightStatusX = bodyX + bodyW - 18

    function pos(i: number) {
      const n = i - 1
      const row = Math.floor(n / cfg.cols)
      const col = n % cfg.cols
      return { x: blockX + col * (portW + gapX), y: topY + row * (portH + gapY), width: portW, height: portH }
    }

    let ports = ''
    for (let i = 1; i <= 16; i++) {
      const p = pos(i)
      portPositions.push({ index: i, x: p.x, y: p.y, width: p.width, height: p.height, type: 'rj45' })
      ports += drawRJ45Port(p, i, 2.3, portW < 20)
    }

    const statuses = [
      ['PoE', '#f59e0b'],
      ['Link', '#22c55e'],
      ['Act', '#3b82f6'],
    ].map((s, i) => `
      <g>
        <circle cx="${rightStatusX}" cy="${topY + 4 + i * 7}" r="1.0" fill="${s[1]}"/>
        <text x="${rightStatusX - 3}" y="${topY + 5.2 + i * 7}" text-anchor="end" font-size="3.0" font-family="Inter, Arial, sans-serif" fill="#8f98a6">${s[0]}</text>
      </g>`).join('')

    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${WIDTH} ${HEIGHT}" width="${WIDTH}" height="${HEIGHT}" shape-rendering="geometricPrecision">
        <defs>
          <linearGradient id="whiteBody_${id}" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#ffffff"/>
            <stop offset="100%" stop-color="#edf1f6"/>
          </linearGradient>
        </defs>
        ${bodyShell()}
        ${unifiLogo()}
        <circle cx="${blueDot.x}" cy="${blueDot.y}" r="3.2" fill="#3b82f6"/>
        <circle cx="${blueDot.x}" cy="${blueDot.y}" r="4.9" fill="none" stroke="#6ba0fb" stroke-width="0.6" opacity="0.7"/>
        ${ports}
        ${statuses}
      </svg>
    `
  }

  // ── Flex-Mini Front ────────────────────────────────────

  function renderFlexMiniFront(): string {
    portPositions.length = 0

    const blueDot = { x: bodyX + 10, y: bodyY + 12 }
    const portW = 18
    const portH = 11
    const gapX = 3
    const totalPortsW = 5 * portW + 4 * gapX
    const startX = bodyX + (bodyW - totalPortsW) / 2
    const y = bodyY + bodyH - 16
    const rightStatusX = startX + totalPortsW + 8

    let ports = ''
    for (let i = 1; i <= 5; i++) {
      const px = startX + (i - 1) * (portW + gapX)
      portPositions.push({ index: i, x: px, y, width: portW, height: portH, type: 'rj45' })
      ports += drawRJ45Port({ x: px, y, width: portW, height: portH }, i, 2.4, false)
    }

    const statuses = [
      ['PoE', '#f59e0b'],
      ['Link', '#22c55e'],
      ['Act', '#3b82f6'],
    ].map((s, i) => `
      <g>
        <circle cx="${rightStatusX}" cy="${y + 3 + i * 5.8}" r="0.95" fill="${s[1]}"/>
        <text x="${rightStatusX + 3}" y="${y + 4 + i * 5.8}" font-size="2.8" font-family="Inter, Arial, sans-serif" fill="#8f98a6">${s[0]}</text>
      </g>`).join('')

    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${WIDTH} ${HEIGHT}" width="${WIDTH}" height="${HEIGHT}" shape-rendering="geometricPrecision">
        <defs>
          <linearGradient id="whiteBody_${id}" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#ffffff"/>
            <stop offset="100%" stop-color="#edf1f6"/>
          </linearGradient>
        </defs>
        ${bodyShell()}
        ${unifiLogo()}
        <circle cx="${blueDot.x}" cy="${blueDot.y}" r="2.5" fill="#3b82f6"/>
        ${ports}
        ${statuses}
      </svg>
    `
  }

  // ── Public API ─────────────────────────────────────────

  function renderFront(): string {
    if (cfg.family === 'pro-rack') return renderProRackFront()
    if (cfg.family === 'lite-desktop') return renderLite16Front()
    return renderFlexMiniFront()
  }

  function renderBack(): string { return '' }

  function getPortPositions(): PortPosition[] {
    // Ensure positions are populated by calling renderFront if needed
    if (portPositions.length === 0) renderFront()
    return [...portPositions]
  }

  const vbHeight = cfg.family === 'pro-rack' ? HEIGHT + 34 : HEIGHT

  return {
    model,
    renderFront,
    renderBack,
    getPortPositions,
    viewBox: { width: WIDTH, height: vbHeight },
  }
}
